import type {
  DataProtectionAnonymizeScope,
  DataProtectionBytesByType,
  DataProtectionEvidenceEstimate,
  DataProtectionExternalSystem,
  DataProtectionNotificationEmail
} from '@app/services/data-protection.service';

export const ANONYMIZE_SCOPES: DataProtectionAnonymizeScope[] = ['advocates', 'campaigns', 'project'];

export interface AnonymizeScopeOption { value: DataProtectionAnonymizeScope; label: string; locked: boolean; }

export const ANONYMIZE_SUMMARY = 'Retains year-over-year report data except for advocates identity';
export const ANONYMIZE_SCOPE_OPTIONS: AnonymizeScopeOption[] = [
  {value: 'advocates', locked: true,
    label: "All advocates/influencers's all identifiable information including headshot, email, logs, "
      + 'IPs, name, company, title, promo code, etc.'},
  {value: 'campaigns', locked: false,
    label: "All campaign assets and info (You won't be able to duplicate campaign for the next year)"},
  {value: 'project', locked: false,
    label: 'All project assets and info except title. Including links, texts, hashtags, dates, etc '
      + "(You won't be able to duplicate project for the next year)"}
];
export const ANONYMIZE_SCOPE_SHORT_LABELS: { [scope: string]: string } =
  {advocates: 'Advocates', campaigns: 'Campaigns', project: 'Project'};

export const EXTERNAL_SYSTEMS: DataProtectionExternalSystem[] = ['google_drive', 'hubspot', 'asana'];
export const EXTERNAL_SYSTEM_LABELS: { [system: string]: string } =
  {google_drive: 'Google Drive', hubspot: 'HubSpot', asana: 'Asana'};
export interface ExternalSystemOption { value: DataProtectionExternalSystem; label: string; description: string; }
export const EXTERNAL_SYSTEM_OPTIONS: ExternalSystemOption[] = [
  {value: 'google_drive', label: 'Google Drive', description: 'Exports, reports, advocate lists, briefs and uploaded files for this project, including copies in shared drives and in the trash.'},
  {value: 'hubspot', label: 'HubSpot', description: 'Contacts, companies, deals, notes, emails and attachments created for this project or its advocates.'},
  {value: 'asana', label: 'Asana', description: 'Tasks, comments and attachments about this project.'}
];

export interface NotificationEmailOption { value: DataProtectionNotificationEmail; label: string; description: string; }
export const NOTIFICATION_EMAIL_OPTIONS: NotificationEmailOption[] = [
  {value: 'approval', label: 'Approval request', description: "To the client's account managers as they are today, with fresh approval links. Links in earlier emails keep working until they expire."},
  {value: 'creator', label: "Notice to the project's creator", description: 'Tells the person who created the project that this request was filed.'},
  {value: 'completion', label: 'Completion email', description: 'To the people this request concerns, with a fresh link to download the evidence.'}
];

export const EVIDENCE_STATUS_LABELS: { [status: string]: string } = {
  collecting: 'Collecting evidence', building: 'Preparing evidence', ready: 'Evidence ready',
  failed: 'Evidence could not be prepared', expired: 'Evidence expired'
};

export function splitList(value: ReadonlyArray<string> | string | null | undefined): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  return typeof value === 'string' ? value.split(',').map((item) => item.trim()).filter(Boolean) : [];
}

export function canonicalScope(value: ReadonlyArray<string> | string | null | undefined): DataProtectionAnonymizeScope[] {
  const picked = splitList(value);
  return ANONYMIZE_SCOPES.filter((scope) => scope === 'advocates' || picked.indexOf(scope) !== -1);
}

export function effectiveScope(row: {request_type: string; anonymize_scope?: ReadonlyArray<string> | string | null}):
  DataProtectionAnonymizeScope[] | null {
  if (row.request_type !== 'anonymize') {
    return null;
  }
  const listed = splitList(row.anonymize_scope);
  return listed.length ? canonicalScope(listed) : ANONYMIZE_SCOPES.slice();
}

export function sameScope(a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean {
  return canonicalScope(a).join(',') === canonicalScope(b).join(',');
}

export function listLabels(value: ReadonlyArray<string> | string | null | undefined, labels: { [key: string]: string }): string {
  return splitList(value).map((item) => labels[item] || item).join(', ');
}

export function defaultResendEmail(status: string, resendable: ReadonlyArray<string>): string | null {
  if (status === 'pending_approval' && resendable.indexOf('approval') !== -1) {
    return 'approval';
  }
  if (status === 'done' && resendable.indexOf('completion') !== -1) {
    return 'completion';
  }
  return resendable.length ? resendable[0] : null;
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return unit === 0 ? `${Math.round(value)} B` : `${value.toFixed(1)} ${units[unit]}`;
}

export function bytesFor(value: DataProtectionBytesByType | undefined, requestType: string): number | null {
  const picked = value !== null && typeof value === 'object' ? value[requestType] : value;
  if (picked === null || picked === undefined || picked === '') {
    return null;
  }
  const bytes = Number(picked);
  return Number.isFinite(bytes) && bytes > 0 ? bytes : null;
}

export function evidenceSizeWarning(source: DataProtectionEvidenceEstimate | null | undefined, requestType: string): string | null {
  const estimate = bytesFor(source ? source.evidence_estimate_bytes : null, requestType);
  const cap = bytesFor(source ? source.evidence_cap_bytes : null, requestType);
  if (estimate === null || cap === null || estimate <= cap) {
    return null;
  }
  return `The evidence file for this request is estimated at ${formatBytes(estimate)}, above the `
    + `${formatBytes(cap)} limit, so it may not be available once the request is done. The request itself still goes ahead.`;
}
